/**
 * StatusBadge — FROZEN PUBLIC CONTRACT
 *
 * @public API (do not change without coordinating agent sign-off):
 *   @Input() variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'default'
 *   @Input() label: string
 *
 * Usage:
 *   <app-status-badge variant="success" label="Active" />
 *
 * Design: rounded-md (NOT rounded-full), 11px text, semantic color fills.
 * Matches the JSX prototype spec (ui.jsx HlmBadge — shape is rounded-md h-5 text-[11px]).
 */
import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export type StatusBadgeVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'default';

@Component({
  selector: 'app-status-badge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      [class]="variantClasses()"
      [style.background-color]="variant() === 'neutral' ? 'rgb(var(--ds-surface-raised, 245 245 244))' : null"
      class="inline-flex items-center justify-center h-5 rounded-md px-2 text-[11px] font-medium whitespace-nowrap shrink-0"
      role="status"
    >{{ label() }}</span>
  `,
})
export class StatusBadgeComponent {
  readonly variant = input<StatusBadgeVariant>('default');
  readonly label = input.required<string>();

  variantClasses(): string {
    const map: Record<StatusBadgeVariant, string> = {
      success: 'bg-green-100 text-green-700',
      warning: 'bg-amber-100 text-amber-700',
      danger:  'bg-red-100 text-red-700',
      info:    'bg-blue-100 text-blue-700',
      neutral: 'text-[rgb(var(--ds-mfg,120_113_108))] border border-[rgb(var(--ds-border,231_229_228))]',
      default: 'bg-[#EFF6FF] text-[#2563EB]',
    };
    return map[this.variant()];
  }
}
